import { useCallback, useMemo } from "react";
import { type SrpProductData } from "~/utils/models";

import { Card } from "~/features/configurator/Linear/components/Card";
import {
  AliasesParameter,
  GPSDParameter,
  OldDesignationParameter,
  PackagingParameter,
  SchaefflerIdParameter,
} from "~/domains/product/components/ProductParams";
import { QuantityInput } from "../../../../../../common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "../../../../../../utils/constants";
import CircleSpinner from "../../../../../../common/components/CircleSpinner/CircleSpinner";
import { getPackagingInformation, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";

import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { getCanUserAddToCart } from "~/features/productDetails/components/ProductSummary/ExtendedPdpSummary";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { NewVersionMessage } from "../../../../../../common/components/Messages/NewVersionMessage";
import { trackUserClickRecommendedProductSectionDetails } from "~/features/productDetails/RecommendedProducts/recommendedProductAnalytics";

import { useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/features/PackageRestriction/SRPPackageRestrictionContext";
import {
  calculateSalesCampaign4CampaignPage,
  useSelectPricingDetails,
} from "~/features/salesCampaign/utils/salesCampaignUtils";
import { getGALocationForSearchItem } from "~/features/search/utils/searchPageUtils";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";

import { Price, type PriceProps } from "~/domains/pricing/components/Price/v3/Price";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { makeSalesAwarePackCodeProvider } from "~/features/salesCampaign/utils/salesAwarePackCodeProvider";
import { SalesDataProvider } from "~/features/salesCampaign/hooks/useSalesData";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { Box, Button, Divider, Stack, styled, Typography } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { useResultTileZoovuParams } from "~/features/search/utils/searchUtils";
import { useAddToCartForm } from "~/features/addToCart/hooks/useAddToCartForm";

import { trackPdpWishlistClick } from "~/app/analytics/ga/ga";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/mui/icons/Cycle";
import parse from "html-react-parser";

import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { SaleEndIndicator } from "~/domains/salesCampaign/components/SaleEndIndicator/SaleEndIndicator";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import Tag from "~/common/components/Tag/Tag";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import { addToCartItemFromSubmitData } from "~/features/addToCart/redux/addToCart/addToCartItemFactory";
import { renderButtonsNew } from "./components/useSrpButtons";
import { constructAvailability } from "./components/SearchResultListItemNew/constructAvailability";
import InfoIcon from "@mui/icons-material/Info";

interface Props {
  isPdp?: boolean;
  productCode: string;
  index: number;
  onProductClick: (area: string, product: SrpProductData) => void;
  searchQuery: string;
}

export const SearchResultListItemCampaign = ({ productCode, onProductClick, isPdp, index }: Props) => {
  const { t } = useTranslation();

  const pageType = useSearchPageType();
  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const onWishlistBtnClick = () => {
    trackPdpWishlistClick(productDescriptor.base.designation, 1);
    openAddToWishlist();
  };

  const { form, productDescriptor, main } = useAddToCartForm({
    productCode,
    fetchAvailability: false,
    fetchPrice: true,
    initialPackCodeProvider: makeSalesAwarePackCodeProvider(null, false),
    descriptorTransform: constructAvailability,
  });

  const priceReference = form.watch("priceReference");

  const product = productDescriptor.base;

  const firstSalesCampaignObj = Object.values(productDescriptor.base.salesCampaign ?? {})?.[0]?.[0];
  const salesCampaignCode = firstSalesCampaignObj?.code;

  const { status: salesStatus, data } = useMemo(
    () => calculateSalesCampaign4CampaignPage({ productDescriptor, salesCampaignCode }),
    [productDescriptor, salesCampaignCode],
  );

  const salePerPack = data?.salePerPack;

  const packCode = form.watch("selectedPackCode");
  const selectedPack = getPackagingInformation(productDescriptor, packCode);
  const packSize = main.selectedPackaging?.packSize ?? selectedPack?.packSize ?? 1;

  const salesData = salePerPack?.[packCode];
  const hasManyPackages = salePerPack ? Object.keys(salePerPack).length > 1 : false;

  const quantity = form.watch("quantity");
  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({
    productInput: product,
    initialQuantity: quantity,
    initialPriceReference: priceReference,
  });

  const productInformationClick = useCallback(
    () => onProductClick("Product information", product),
    [onProductClick, product],
  );

  let isPackageSizeInvalid = false;
  if (selectedPack) {
    isPackageSizeInvalid = quantity === 0 || quantity % packSize > 0;
  }

  const userCanAddToCart = getCanUserAddToCart(productDescriptor, selectedPack);

  let exceedsDiscontinuedQuantity = false;
  if (salesData) {
    exceedsDiscontinuedQuantity = salesData.isDiscontinued && quantity > salesData.regularStock;
  }

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const addToCartDisabled =
    webservicesDisabled ||
    !userCanAddToCart ||
    isPackageSizeInvalid ||
    productDescriptor.isLoading ||
    productDescriptor.isError ||
    exceedsDiscontinuedQuantity;

  const hasSales = data?.hasSales;
  const someSaleHasStock = Object.values(data?.salePerPack ?? {}).some((sale) => !sale.soldOut);
  const tagColour = someSaleHasStock ? "active" : "inactive";

  const packaging = getPackagingInformation(productDescriptor, packCode);

  const { price, oldPrice, netPrice, salesPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity,
    priceReference,
    productCode,
    salesData,
    saleActivated: true,
  });

  const isSoldOut = salesData?.soldOut ?? false;
  const salesStock = salesData?.salesStock ?? 0;
  const exceedsSaleQty = salesStock > 0 && quantity > salesStock && !isSoldOut;

  const hasNotifications = isPackageSizeInvalid || isSoldOut || exceedsSaleQty;

  const tariffSurcharge = isTariffSurchargeEnabled() && netPrice?.tariffSurcharge;

  const linkProps = {
    onClick: productInformationClick,
  };

  const doAddToCart = async () => {
    const formValues = form.getValues();

    if (isPdp) {
      trackUserClickRecommendedProductSectionDetails(product.designation);
    }

    const submitData = {
      formValues: {
        quantity: formValues.quantity,
        priceReference: formValues.priceReference,
        selectedPackCode: formValues.selectedPackCode,
        salePriceEnabled: formValues.salePriceEnabled,
        optionalParts: {},
      },
      derived: {
        descriptor: productDescriptor,
        selectedPack: selectedPack!,
        selectedPackaging: main.selectedPackaging,
        netPrice,
        totalQuantity: 1,
      },
      optionalParts: {},
    };

    void addToCart({
      items: [
        addToCartItemFromSubmitData({
          submitData,
          productCode,
          restrictionQuery: "",
          context: { page: "CAMPAIGN" },
        }),
      ],
      page: "CAMPAIGN",
      source: "unknown",
    }).catch(() => undefined);
  };

  const buttons = renderButtonsNew(
    productDescriptor,
    {
      atc: {
        onClick: doAddToCart,
        disabled: addToCartDisabled,
      },
      wishlist: {
        onClick: onWishlistBtnClick,
      },
    },
    {
      sideButton: true,
      maxButtons: 2,
    },
  );
  const mainAction = buttons.buttons[0];
  const sideAction = buttons.sideButtons[0];

  return (
    <SRPPackageRestrictionContext productCode={productCode}>
      <SalesDataProvider value={data}>
        <ProductDescriptorContext value={productDescriptor}>
          {addToWishlistModal}
          {product.displayMMDAssistant && (
            <MMDAssistant
              isPDP={false}
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
              }}
            />
          )}
          <ItemRoot
            className={"jsSearchResultProduct"}
            data-test-id={`search-result-item`}
            {...useResultTileZoovuParams(product)}
          >
            {hasSales && <FloatingSaleTag variant={tagColour} />}

            <Box sx={{ alignSelf: "flex-start" }}>
              <Product.PdpLink onClick={productInformationClick}>
                <Product.PrimaryImageWithLogos clickable={false} />
              </Product.PdpLink>
            </Box>

            <Stack gap={2} flex={1}>
              <Stack gap={0.5}>
                <Box display="flex" justifyContent="space-between">
                  <DesignationWrapper>
                    <Product.FullDesignation linkProps={linkProps} />
                  </DesignationWrapper>
                  <SaleEndIndicator.V2 endDate={salesData?.endDate} />
                </Box>
                <Product.MMDTag />
                <Product.ShortDescription clickable={false} />
              </Stack>
              <ParametersStack gap={1}>
                <OldDesignationParameter variant="horizontal" label={t("product.oldDesignations")} />
                <SchaefflerIdParameter variant="horizontal" />
                <GPSDParameter variant="horizontal" product={productDescriptor.base} />
                <AliasesParameter variant="horizontal" mode={"13-digits"} />
                {!hasManyPackages && packaging && (
                  <PackagingParameter
                    variant="horizontal"
                    productDescriptor={productDescriptor}
                    packagingInfo={packaging}
                  />
                )}
              </ParametersStack>

              {product.reconditionable && (
                <RichTooltip
                  content={
                    <Typography>
                      {parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                    </Typography>
                  }
                  actions={[
                    <Button href={URL_RECONDITIONABLE} variant="text">
                      {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                    </Button>,
                  ]}
                >
                  <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
                    {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                  </Tag>
                </RichTooltip>
              )}

              {isProductComparisonCheckboxVisible(product) && (
                <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
              )}

              <NewVersionMessage baseProduct={product} />

              {hasNotifications && (
                <Stack gap={1}>
                  {isPackageSizeInvalid && (
                    <p className={"f-error-red m-0"}>
                      <InfoIcon className={"icon-margin"} sx={{ fontSize: 20 }} />
                      {t("addToCartPopup.mismatchedQty")}
                    </p>
                  )}
                  {isSoldOut && (
                    <Alert severity="info">
                      {salesData?.fullySoldOut
                        ? t("feature.salesCampaign.soldOut.full")
                        : t("feature.salesCampaign.soldOut.partial")}
                    </Alert>
                  )}
                  {exceedsSaleQty && <Alert severity="info">{t("feature.salesCampaign.exceedsQty")}</Alert>}
                </Stack>
              )}
            </Stack>

            <Divider orientation="vertical" flexItem />

            <Stack gap={2} sx={{ width: "326px" }}>
              {productDescriptor.isLoading && <CircleSpinner />}

              {!isProductPMA(productDescriptor) && (
                <Stack gap={0}>
                  <Stack direction="row" gap={1}>
                    <CurrentPrice
                      price={price}
                      zeroText={t("product.priceOnRequest")}
                      tariffSurcharge={tariffSurcharge}
                      soldOut={isSoldOut && !!oldPrice}
                    />
                    <SaleDiscountIndicator.V2
                      soldOut={isSoldOut}
                      discountFormatted={discountFormatted}
                      variant="titleLarge"
                    />
                  </Stack>

                  {oldPrice && <OldPrice price={oldPrice} soldOut={isSoldOut} />}

                  <Price.NetPriceUnit price={netPrice} variant={"full"} surchargeEnabled={false} />
                  {tariffSurcharge && (
                    <Typography variant="labelSmall">{t("price.tariff.surcharge.applicable")}</Typography>
                  )}
                </Stack>
              )}

              {salesData && <SaleQuantityIndicator.V2 quantity={salesData.salesStock} />}

              <QuantityInput
                variant="compact"
                min={packSize}
                max={MAX_QUANTITY}
                value={quantity}
                disabled={
                  productDescriptor.isAvailabilityLoading ||
                  productDescriptor.isAvailabilityError ||
                  !product.quantityVisible
                }
                step={packSize}
                data-test-id={"plp-srp-list-item-input-quantity"}
                onValueChange={(value) => form.setValue("quantity", value, { shouldTouch: true, shouldDirty: true })}
                error={isPackageSizeInvalid}
              />

              <Stack direction="row" gap={2}>
                {sideAction}
                {mainAction}
              </Stack>
            </Stack>
          </ItemRoot>
        </ProductDescriptorContext>
      </SalesDataProvider>
    </SRPPackageRestrictionContext>
  );
};

const ItemRoot = styled(Card)({
  padding: "16px",
  display: "flex",
  gap: "16px",
});

const FloatingSaleTag = styled(SaleTag)({
  position: "absolute",
  top: "0",
  left: "0",
  zIndex: 2,
});

const CurrentPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.HeadlineSmall),
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));

const DesignationWrapper = styled("div")(({ theme }) => ({
  ...theme.typography.titleLarge,
  fontWeight: "bold",
  wordBreak: "break-word",
}));

const ParametersStack = styled(Stack)(({ theme }) => ({
  "& .scha__param__value": {
    ...theme.typography.bodySmall,
  },
}));
